/**
 * communications.service.ts
 *
 * Typed API call wrappers for the communications domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope.
 */
import { apiClient } from './apiClient';
import { presignLargeFiles } from './uploads.service';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';
import { facilityScopeParams, type FacilityScopeParams } from '@/lib/facilityScopeParams';
import type { ReadSource } from '@/lib/readSource';

// ─── Types (mirrors server communications.types.ts) ───────────────────────────

export interface ChatUser {
  userId: number;
  firstName: string;
  lastName: string;
  role: string | null;
  unreadCount: number;
}

export interface ChatMessage {
  messageId: number;
  threadId: number;
  fromUserId: number;
  fromFirstName: string;
  fromLastName: string;
  toUserId: number;
  message: string;
  isRead: boolean;
  sentAt: string;
}

export interface SMSTemplate {
  templateId: number;
  accountId: number;
  templateName: string;
  message: string;
  categoryId: number | null;
  categoryName: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface SMSTemplateCategory {
  categoryId: number;
  accountId: number;
  categoryName: string;
  sortOrder: number;
}

export interface EmailTemplate {
  templateId: number;
  accountId: number;
  contentName: string;
  subject: string;
  body: string;
  categoryId: number | null;
  categoryName: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface EmailTemplateCategory {
  categoryId: number;
  accountId: number;
  categoryName: string;
  sortOrder: number;
}

export type CommLogType =
  | 'email_sent'
  | 'email_received'
  | 'sms_sent'
  | 'sms_received'
  | 'call_logged'
  | 'chat'
  | 'note'
  | 'stage_change'
  | 'document_sent'
  | 'autoresponder';

/**
 * One entry in a message's read-audit trail: who marked it as read and when.
 *
 * `readAt` is the same naive local-time string every other timestamp in this
 * payload uses (no offset), so format it the way message timestamps are
 * formatted rather than treating it as UTC.
 */
export interface CommLogReadEvent {
  userId: number;
  name: string;
  readAt: string;
  /**
   * The screen the read came from, or null when none was recorded: reads from
   * before the screen was saved, and callers that do not send one. Optional
   * because responses that predate the field omit it.
   */
  source?: ReadSource | null;
}

export interface CommLogEntry {
  commLogId: number;
  accountId: number;
  planId: number | null;
  planDisplayId?: string | null;
  patientId: number | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientEmail?: string | null;
  patientPhone?: string | null;
  /**
   * Avatar availability for the inbox row: a URL when the patient has a photo,
   * null when they do not (or for orphan rows). Absent on responses that
   * predate the field, which `usePatientPhoto` treats as "unknown".
   */
  patientPhotoUrl?: string | null;
  /** Patient's facility (patients_addons.facilityID -> facilities.name); null for orphan rows. */
  facilityId?: number | null;
  facilityName?: string | null;
  userId: number | null;
  userFirstName: string | null;
  userLastName: string | null;
  type: CommLogType;
  subject: string | null;
  message: string;
  externalRef: string | null;
  deliveryStatus: string | null;
  createdAt: string;
  isDeleted: boolean;
  wasRead?: number | boolean;
  /** Who marked this message as read, oldest first. Empty means nothing was recorded, NOT that nobody read it. */
  readHistory?: CommLogReadEvent[];
  fromPatient?: number | boolean;
  authorFirstName?: string | null;
  authorLastName?: string | null;
  emailBody?: string;
  smsMessage?: string;
  /** Resolved stage name for stage_change entries. */
  stageName?: string | null;
  /** Inbound sender display name (used when patientId is null/0). */
  strActivity?: string;
  /** Inbound sender contact (email for inbound emails). */
  strQuoteInfo?: string;
}

export type MessageFolder = 'unread' | 'read' | 'sent' | 'undeliverable';

export interface MessageFolderCounts {
  unread: number;
  read: number;
  sent: number;
  undeliverable: number;
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export function getChatUsers(): Promise<ChatUser[]> {
  return apiClient
    .get<ApiResponse<ChatUser[]>>('/communications/chat/users')
    .then((r) => r.data.data);
}

export function getMessages(page = 1): Promise<PaginatedResponse<ChatMessage>> {
  return apiClient
    .get<PaginatedResponse<ChatMessage>>('/communications/chat/messages', {
      params: { page },
    })
    .then((r) => r.data);
}

export function sendMessage(toUserId: number, message: string): Promise<{ messageId: number }> {
  return apiClient
    .post<ApiResponse<{ messageId: number }>>('/communications/chat/messages', {
      toUserId,
      message,
    })
    .then((r) => r.data.data);
}

export function markMessageRead(messageId: number): Promise<void> {
  return apiClient
    .patch(`/communications/chat/messages/${messageId}/read`)
    .then(() => undefined);
}

export function getUnreadCount(): Promise<number> {
  return apiClient
    .get<ApiResponse<{ count: number }>>('/communications/chat/unread-count')
    .then((r) => r.data.data.count);
}

// ─── SMS Templates ────────────────────────────────────────────────────────────

export function getSMSTemplates(): Promise<SMSTemplate[]> {
  return apiClient
    .get<ApiResponse<SMSTemplate[]>>('/sms-templates')
    .then((r) => r.data.data);
}

export function createSMSTemplate(
  data: Pick<SMSTemplate, 'templateName' | 'message'> &
    Partial<Pick<SMSTemplate, 'categoryId' | 'active' | 'sortOrder'>>,
): Promise<SMSTemplate> {
  return apiClient
    .post<ApiResponse<SMSTemplate>>('/sms-templates', data)
    .then((r) => r.data.data);
}

export function updateSMSTemplate(
  id: number,
  data: Partial<Pick<SMSTemplate, 'templateName' | 'message' | 'categoryId' | 'active' | 'sortOrder'>>,
): Promise<SMSTemplate> {
  return apiClient
    .put<ApiResponse<SMSTemplate>>(`/sms-templates/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteSMSTemplate(id: number): Promise<void> {
  return apiClient
    .delete(`/sms-templates/${id}`)
    .then(() => undefined);
}

export function getSMSCategories(): Promise<SMSTemplateCategory[]> {
  return apiClient
    .get<ApiResponse<SMSTemplateCategory[]>>('/sms-template-categories')
    .then((r) => r.data.data);
}

export function sendSMS(data: {
  patientId: number;
  planId?: number | null;
  toPhone: string;
  message: string;
  recipients?: string[];
  channel?: 'sms' | 'whatsapp';
  /**
   * The message being replied to, when this send is an inline reply.
   *
   * Threading happens here rather than on the comm-log reply endpoint, which
   * records SMS and WhatsApp without ever dispatching them. The server writes
   * this into comm_log.replyID, which is what nests the reply under its parent.
   */
  replyId?: string;
  /**
   * The screen this reply is sent from. A reply marks the message it answers
   * as read, and the read line shows where. Only meaningful with `replyId`.
   */
  readSource?: ReadSource;
  /**
   * Why this text is sent. `gi_form` marks a GI form texted by hand, which
   * counts toward the automatic GI text's once-a-day cap. Never blocks the send.
   */
  purpose?: 'gi_form';
  /**
   * The practice number staff chose to send from (any written form). The
   * server sends only from one of the account's own numbers and refuses
   * anything else. Left out, the server picks the number as it always has.
   */
  fromNumber?: string;
}): Promise<{ messageSid: string }> {
  return apiClient
    .post<ApiResponse<{ messageSid: string }>>('/communications/sms/send', data)
    .then((r) => r.data.data);
}

/** One number a text can be sent from, as the comm log composer lists it. */
export interface SendFromOption {
  /** 10 digits. */
  number: string;
  kind: 'reply' | 'confirm' | 'marketing';
  label: string;
  facilityId: number | null;
  facilityName: string | null;
  /** What choosing this line changes, shown while it is selected. */
  hint: string | null;
}

export interface SendFromOptions {
  /** The patient's facility first, then the practice's lines, then other facilities. */
  options: SendFromOption[];
  /** The number a send with no choice goes out from (10 digits), or null. */
  defaultNumber: string | null;
}

export function getSendFromOptions(patientId: number): Promise<SendFromOptions> {
  return apiClient
    .get<ApiResponse<SendFromOptions>>('/communications/sms/send-from-options', { params: { patientId } })
    .then((r) => r.data.data);
}

/** One number a call can show the patient: a reply line of the practice or one of its facilities. */
export interface CallerIdOption {
  /** 10 digits. */
  number: string;
  kind: 'reply';
  /** The facility's name, or "Practice line" plus its slot. */
  label: string;
  facilityId: number | null;
  facilityName: string | null;
}

export interface CallerIdOptions {
  /** The patient's facility first, then the practice's lines, then other facilities. */
  options: CallerIdOption[];
  /** The number a call with no choice shows (10 digits), or null. */
  defaultNumber: string | null;
}

export function getCallerIdOptions(patientId: number): Promise<CallerIdOptions> {
  return apiClient
    .get<ApiResponse<CallerIdOptions>>('/voice/caller-id-options', { params: { patientId } })
    .then((r) => r.data.data);
}

// ─── Email Templates ──────────────────────────────────────────────────────────

export function getEmailTemplates(): Promise<EmailTemplate[]> {
  return apiClient
    .get<ApiResponse<EmailTemplate[]>>('/email-templates')
    .then((r) => r.data.data);
}

export function createEmailTemplate(
  data: Pick<EmailTemplate, 'contentName' | 'subject' | 'body'> &
    Partial<Pick<EmailTemplate, 'categoryId' | 'active' | 'sortOrder'>>,
): Promise<EmailTemplate> {
  return apiClient
    .post<ApiResponse<EmailTemplate>>('/email-templates', data)
    .then((r) => r.data.data);
}

export function updateEmailTemplate(
  id: number,
  data: Partial<
    Pick<EmailTemplate, 'contentName' | 'subject' | 'body' | 'categoryId' | 'active' | 'sortOrder'>
  >,
): Promise<EmailTemplate> {
  return apiClient
    .put<ApiResponse<EmailTemplate>>(`/email-templates/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteEmailTemplate(id: number): Promise<void> {
  return apiClient
    .delete(`/email-templates/${id}`)
    .then(() => undefined);
}

export function getEmailCategories(): Promise<EmailTemplateCategory[]> {
  return apiClient
    .get<ApiResponse<EmailTemplateCategory[]>>('/email-template-categories')
    .then((r) => r.data.data);
}

export async function sendEmail(data: {
  patientId: number;
  planId?: number | null;
  toEmail: string;
  toName?: string | null;
  fromName?: string;
  subject: string;
  htmlContent: string;
  recipients?: string[];
  replyId?: number;
  files?: File[];
  existingDocuments?: string[];
}): Promise<{ sent: boolean }> {
  const { files, recipients, existingDocuments, ...rest } = data;
  // Multipart so on-demand attachments ride along with the email. apiClient
  // strips Content-Type for FormData so the boundary is set automatically.
  const form = new FormData();
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, String(value));
  });
  (recipients ?? []).forEach((token) => form.append('recipients[]', token));
  // Attachments > 4 MB are uploaded directly to S3 and sent as s3Keys[] to
  // bypass Lambda's 6 MB payload cap; smaller ones ride along as attachments[].
  const { multipartFiles, s3Keys } = await presignLargeFiles(files ?? []);
  multipartFiles.forEach((file) => form.append('attachments[]', file));
  s3Keys.forEach((key) => form.append('s3Keys[]', key));
  (existingDocuments ?? []).forEach((name) => form.append('existingDocuments[]', name));

  return apiClient
    .post<ApiResponse<{ sent: boolean }>>('/communications/email/send', form)
    .then((r) => r.data.data);
}

export async function replyToCommLog(
  commLogId: string | number,
  data: {
    message: string;
    subject?: string;
    recipients?: string[];
    files?: File[];
    existingDocuments?: string[];
    /** The screen the reply is sent from; the read line shows it. */
    readSource?: ReadSource;
  },
): Promise<unknown> {
  // Multipart variant of the reply endpoint so email replies can carry
  // attachments. The JSON-only v5 api() helper can't do FormData.
  const form = new FormData();
  form.append('message', data.message);
  if (data.subject) form.append('subject', data.subject);
  if (data.readSource) form.append('readSource', data.readSource);
  (data.recipients ?? []).forEach((token) => form.append('recipients[]', token));
  // Attachments > 4 MB go directly to S3 as s3Keys[] (Lambda 6 MB payload cap).
  const { multipartFiles, s3Keys } = await presignLargeFiles(data.files ?? []);
  multipartFiles.forEach((file) => form.append('attachments[]', file));
  s3Keys.forEach((key) => form.append('s3Keys[]', key));
  (data.existingDocuments ?? []).forEach((name) => form.append('existingDocuments[]', name));

  return apiClient
    .post<ApiResponse<unknown>>(`/communications/comm-log/${commLogId}/reply`, form)
    .then((r) => r.data.data);
}

export interface RecipientPickerOption {
  token: string;
  label: string;
  title?: string;
  userId?: number;
}

export interface RecipientPickerOptions {
  planTokens: RecipientPickerOption[];
  groups: RecipientPickerOption[];
  users: RecipientPickerOption[];
}

export function getRecipientOptions(planId?: number | null): Promise<RecipientPickerOptions> {
  return apiClient
    .get<ApiResponse<RecipientPickerOptions>>('/communications/recipient-options', {
      params: planId ? { planId } : undefined,
    })
    .then((r) => r.data.data);
}

// ─── Communication Log — scoped payload ──────────────────────────────────────

/**
 * Payload for manually logging a contact/interaction via the scoped endpoint.
 * typeId maps to CommLogType: note=4, call=3, email=1, sms=2
 */
export interface LogContactPayload {
  planId?: number;
  patientId?: number;
  type: CommLogType;
  subject?: string;
  message: string;
  isPrivate?: boolean;
}

// ─── Communication Log ────────────────────────────────────────────────────────

export function getCommLog(
  filters: {
    patientId?: number;
    planId?: number;
    type?: CommLogType;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  },
  pagination: { page?: number; pageSize?: number } = {},
): Promise<PaginatedResponse<CommLogEntry>> {
  return apiClient
    .get<PaginatedResponse<CommLogEntry>>('/communications/log', {
      params: { ...filters, ...pagination },
    })
    .then((r) => r.data);
}

export function getMessageInbox(
  filters: {
    folder?: MessageFolder;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    type?: CommLogType | 'whatsapp';
    facilityId?: number;
    /**
     * The whole facility selection. When given it replaces `facilityId`: one id
     * is sent as `facilityId` (the request a single-facility user always sent),
     * several as `facilityIds[]`, none as no facility param.
     */
    facilityIds?: readonly number[];
    /**
     * Narrow to rows the signed-in user is tagged on. Orthogonal to `folder` —
     * it combines with any of them, and leaves "unread" meaning practice-wide
     * unread. Omitted rather than sent as 0 when off, so the default request is
     * byte-identical to what it was before the filter existed.
     */
    taggedToMe?: boolean;
  },
  pagination: { page?: number; pageSize?: number; includeTotal?: boolean } = {},
): Promise<PaginatedResponse<CommLogEntry>> {
  const scope: FacilityScopeParams = filters.facilityIds !== undefined
    ? facilityScopeParams(filters.facilityIds)
    : { facilityId: filters.facilityId };
  return apiClient
    .get<PaginatedResponse<CommLogEntry>>('/communications/log', {
      params: {
        channels: 'messages',
        folder: filters.folder,
        search: filters.search,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        type: filters.type,
        // Same slot as before, so a one-facility query string is unchanged.
        facilityId: scope.facilityId,
        facilityIds: scope.facilityIds,
        taggedToMe: filters.taggedToMe ? 1 : undefined,
        ...pagination,
      },
    })
    .then((r) => r.data);
}

/**
 * Per-user Inbox feed for the dashboard "Inbox" card.
 *
 * Backed by GET /communications/log?folder=inbox, which returns every
 * comm_log row where the logged-in user has a comm_log_recipients
 * entry — any type, any direction. It applies no read filter of its
 * own, so `readStatus: 'unread'` is what narrows it to work still
 * outstanding; omit it and the caller gets everything ever addressed
 * to the user, with unread sorted on top (server orders by
 * clr.wasRead ASC, cl.date DESC) and no date window at all.
 *
 * Callers that render a count beside the list must take it from
 * getInboxCounts(), which is scoped to this same recipient population.
 * /dashboard/summary's unreadMessages is a different query entirely
 * (practice-wide, inbound only) and will not agree with these rows.
 *
 * The response's `wasRead` field reflects the user's per-recipient
 * read state (clr.wasRead), so it's safe to bold unread rows even
 * when other recipients of the same message have already read it.
 */
export function getMyInbox(
  filters: {
    pageSize?: number;
    page?: number;
    readStatus?: 'unread' | 'all';
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    search?: string;
    /**
     * Channel tokens to narrow the feed to, e.g. ['email', 'note']. Sent as
     * a CSV the server unions into legacy typeIDs; an empty array is omitted
     * entirely so "nothing ticked" means "every type", not "no rows".
     */
    types?: string[];
  } = {},
): Promise<PaginatedResponse<CommLogEntry>> {
  const params: Record<string, string | number> = {
    channels: 'messages',
    folder: 'inbox',
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 7,
    includeTotal: 1,
  };
  if (filters.readStatus && filters.readStatus !== 'all') {
    params.readStatus = filters.readStatus;
  }
  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.types && filters.types.length > 0) {
    params.type = filters.types.join(',');
  }
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
    params.sortDir = filters.sortDir ?? 'desc';
  }
  return apiClient
    .get<PaginatedResponse<CommLogEntry>>('/communications/log', { params })
    .then((r) => r.data);
}

export interface InboxCounts {
  unread: number;
  all: number;
}

export function getInboxCounts(): Promise<InboxCounts> {
  return apiClient
    .get<ApiResponse<InboxCounts>>('/communications/log/inbox-counts')
    .then((r) => r.data.data);
}

export function getMessageCounts(search?: string): Promise<MessageFolderCounts> {
  return apiClient
    .get<ApiResponse<MessageFolderCounts>>('/communications/log/counts', {
      params: search ? { search } : undefined,
    })
    .then((r) => r.data.data);
}

export function getCommLogEntry(logId: number): Promise<CommLogEntry> {
  return apiClient
    .get<ApiResponse<CommLogEntry>>(`/communications/log/${logId}`)
    .then((r) => r.data.data);
}

export function markCommLogEntryRead(logId: number, readSource: ReadSource): Promise<CommLogEntry> {
  return apiClient
    .patch<ApiResponse<CommLogEntry>>(`/communications/log/${logId}/read`, { readSource })
    .then((r) => r.data.data);
}

/** Mark a comm_log entry as read for the current user only (per-recipient). */
export function markInboxEntryRead(logId: number, readSource: ReadSource): Promise<CommLogEntry> {
  return apiClient
    .patch<ApiResponse<CommLogEntry>>(`/communications/log/${logId}/recipient-read`, { readSource })
    .then((r) => r.data.data);
}

/** Bulk mark multiple comm_log entries as read for the current user (per-recipient). */
export function bulkMarkInboxRead(ids: number[], readSource: ReadSource): Promise<{ updated: number }> {
  return apiClient
    .patch<ApiResponse<{ updated: number }>>('/communications/log/bulk-recipient-read', { ids, readSource })
    .then((r) => r.data.data);
}

// ─── @Mentions ────────────────────────────────────────────────────────────────
// A mention is a tag a colleague added in Prospera. One row per patient
// conversation; "unread" means the signed-in user has not opened it since.

export interface CommLogMention {
  /** True while any mention in this conversation is unopened by the user. */
  unread: boolean;
  mentionedByName: string;
  mentionedAt: string;
  patientId: number;
}

/** A Communication Center row for the latest mention in a conversation. */
export interface MentionConversation extends CommLogEntry {
  mention: CommLogMention;
}

export interface MentionListMeta {
  page: number;
  perPage: number;
  hasNext: boolean;
  /** Conversations with an unopened mention, under the same facility scope; null when the count failed. */
  unreadConversations: number | null;
}

export interface MentionConversationsResponse {
  data: MentionConversation[];
  meta: MentionListMeta;
}

/** Slim unread-only row for the nav's short list; no message body. */
export interface MentionPreviewRow {
  patientId: number;
  patientName: string;
  facilityName: string | null;
  commLogId: number;
  mentionedAt: string;
  mentionedByName: string;
}

export interface MentionPreviewResponse {
  data: MentionPreviewRow[];
  meta: MentionListMeta;
}

/** Which mentions to mark as read: one patient's conversation, or all in scope. */
export type MentionReadSelection = { patientId: number } | { all: true };

export interface MentionReadResult {
  updated: number;
  /** The new count, or null when recounting failed after the clear went through. */
  unreadConversations: number | null;
}

/**
 * Conversations where the signed-in user was mentioned, unopened first, then
 * newest first. Facility scope as `facilityScopeParams`.
 */
export function getMentionConversations(
  params: { facilityIds?: readonly number[]; page?: number; pageSize?: number } = {},
): Promise<MentionConversationsResponse> {
  return apiClient
    .get<MentionConversationsResponse>('/communications/log/mentions', {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...facilityScopeParams(params.facilityIds ?? []),
      },
    })
    .then((r) => r.data);
}

/** Unopened mentioned conversations for the nav drop-down, newest first. */
export function getMentionPreview(
  params: { facilityIds?: readonly number[]; pageSize?: number } = {},
): Promise<MentionPreviewResponse> {
  return apiClient
    .get<MentionPreviewResponse>('/communications/log/mentions', {
      params: {
        preview: 1,
        pageSize: params.pageSize,
        ...facilityScopeParams(params.facilityIds ?? []),
      },
    })
    .then((r) => r.data);
}

/**
 * Mark mentions as read for the signed-in user. `{ patientId }` clears that
 * patient's conversation wherever it is, so no facility scope is sent;
 * `{ all: true }` clears only what the given facility scope shows.
 *
 * Clearing marks the messages read, so the request names the screen it came
 * from, as the other mark-read calls do; the read line shows it.
 */
export function markMentionsRead(
  selection: MentionReadSelection,
  readSource: ReadSource,
  facilityIds?: readonly number[],
): Promise<MentionReadResult> {
  const scope = 'all' in selection ? facilityScopeParams(facilityIds ?? []) : {};
  return apiClient
    .patch<ApiResponse<MentionReadResult>>('/communications/log/mentions/read', { ...selection, readSource }, {
      params: Object.keys(scope).length > 0 ? scope : undefined,
    })
    .then((r) => r.data.data);
}

// ─── Overview ─────────────────────────────────────────────────────────────────

/** One facility's share of the overview counts. facilityId null = no facility on file. */
export interface CommunicationOverviewFacility {
  facilityId: number | null;
  facilityName: string | null;
  unreadMessages: number;
  mentions: number;
}

export interface CommunicationOverview {
  /** The nav badge's unread-messages count, under the same facility scope. */
  unreadMessages: number;
  /** Conversations with an unopened mention of the signed-in user. */
  mentions: number;
  /** Facilities with anything to count, busiest first; the null-facility row last. */
  byFacility: CommunicationOverviewFacility[];
}

/** Counts for the Communication Center overview page. Facility scope as `facilityScopeParams`. */
export function getCommunicationOverview(facilityIds: readonly number[]): Promise<CommunicationOverview> {
  const scope = facilityScopeParams(facilityIds);
  return apiClient
    .get<ApiResponse<CommunicationOverview>>('/communications/log/overview', {
      params: Object.keys(scope).length > 0 ? scope : undefined,
    })
    .then((r) => r.data.data);
}

export interface PatientMatchCandidate {
  patientId: number;
  displayId: number | string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  homePhone: string | null;
  birthDate: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  matchScore: number;
  matches: {
    firstName: boolean;
    lastName: boolean;
    name: boolean;
    email: boolean;
    phone: boolean;
  };
}

export interface MessageMatchCandidatesResponse {
  candidates: PatientMatchCandidate[];
  sender: {
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

export function getMessageMatchCandidates(logId: number): Promise<MessageMatchCandidatesResponse> {
  return apiClient
    .get<ApiResponse<PatientMatchCandidate[]> & { meta?: { sender?: MessageMatchCandidatesResponse['sender'] } }>(
      `/communications/log/${logId}/match-candidates`,
    )
    .then((r) => ({
      candidates: r.data.data ?? [],
      sender: r.data.meta?.sender ?? { name: '', firstName: '', lastName: '', email: '', phone: '' },
    }));
}

export function mergeCommLogEntry(
  logId: number,
  payload: { patientId: number; planId?: number },
): Promise<CommLogEntry> {
  return apiClient
    .post<ApiResponse<CommLogEntry>>(`/communications/comm-log/${logId}/merge`, {
      patientId: payload.patientId,
      planId: payload.planId ?? 0,
    })
    .then((r) => r.data.data);
}

export function replyToCommLogEntry(
  logId: number,
  data: { subject?: string; message: string },
): Promise<CommLogEntry> {
  return apiClient
    .post<ApiResponse<CommLogEntry>>(`/communications/comm-log/${logId}/reply`, data)
    .then((r) => r.data.data);
}

export function addCommLogEntry(data: {
  planId?: number | null;
  patientId?: number | null;
  type: CommLogType;
  subject?: string | null;
  message: string;
  externalRef?: string | null;
  deliveryStatus?: string | null;
}): Promise<{ commLogId: number }> {
  return apiClient
    .post<ApiResponse<{ commLogId: number }>>('/communications/log', data)
    .then((r) => r.data.data);
}

// ─── Communication Log — scoped helpers ──────────────────────────────────────

/** Paginated comm log filtered to a single plan. */
export function getCommLogByPlan(
  planId: number,
  page = 1,
): Promise<PaginatedResponse<CommLogEntry>> {
  return apiClient
    .get<PaginatedResponse<CommLogEntry>>(`/communications/comm-log/plan/${planId}`, {
      params: { page },
    })
    .then((r) => r.data);
}

/** Paginated comm log filtered to a single patient. */
export function getCommLogByPatient(
  patientId: number,
  page = 1,
): Promise<PaginatedResponse<CommLogEntry>> {
  return apiClient
    .get<PaginatedResponse<CommLogEntry>>(`/communications/comm-log/patient/${patientId}`, {
      params: { page },
    })
    .then((r) => r.data);
}

/** Manually log a contact interaction (note, call, email, SMS). */
export function logContact(
  data: LogContactPayload,
): Promise<{ commLogId: number }> {
  return apiClient
    .post<ApiResponse<{ commLogId: number }>>('/communications/comm-log', data)
    .then((r) => r.data.data);
}

/** Update subject/message on an existing comm log entry. */
export function updateLogEntry(
  logId: number,
  data: { subject?: string | null; message?: string },
): Promise<CommLogEntry> {
  return apiClient
    .put<ApiResponse<CommLogEntry>>(`/communications/comm-log/${logId}`, data)
    .then((r) => r.data.data);
}

/** Soft-delete a comm log entry. */
export function deleteLogEntry(logId: number): Promise<void> {
  return apiClient
    .delete(`/communications/comm-log/${logId}`)
    .then(() => undefined);
}
